import { useEffect, useMemo, useState } from "react";
import { useNavigate, Routes, Route, Navigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import AdminPurge from "@/pages/AdminPurge";
import AdminAccountsCard from "@/components/AdminAccountsCard";
import ResourceImageUploader from "@/components/ResourceImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BadgeCheck, Crown, Trash2, Plus, Search, UserX, UserCheck,
  Users, Megaphone, CalendarDays, Trophy, TrendingUp,
  ShieldCheck, Award, X, BellRing, Eye, Pencil,
  Heart, EyeOff, Image as ImageIcon, ExternalLink, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { PremiumBadge, VerifiedBadge } from "@/components/Badges";
import AdminFeatured from "@/pages/AdminFeatured";
import AdminDashboardControl from "@/pages/AdminDashboardControl";
import AdminConcoursV2 from "@/pages/AdminConcoursV2";
import AdminModeration from "@/pages/AdminModeration";
import AdminEventParticipants from "@/pages/AdminEventParticipants";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminCMS from "@/pages/AdminCMS";
import DashboardHeroToggle from "@/components/DashboardHeroToggle";
import { EVENT_TYPES, labelEventType } from "@/lib/roleCategories";

/* ============================= OVERVIEW ============================= */
function Overview() {
  const [stats, setStats] = useState({});
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/activity").then(({ data }) => setActivity(data));
  }, []);

  const cards = [
    { label: "Talents",        value: stats.talents,        icon: Users,        color: "#C68E59" },
    { label: "Créateurs", value: stats.pros,           icon: Megaphone,    color: "#A26F3E" },
    { label: "Premium",        value: stats.premium,        icon: Crown,        color: "#D4A574" },
    { label: "Vérifiés",       value: stats.verified_pros,  icon: BadgeCheck,   color: "#6E4B2A" },
    { label: "Collabs actives",value: stats.active_collabs, icon: TrendingUp,   color: "#C68E59" },
    { label: "Candidatures",   value: stats.applications,   icon: Award,        color: "#A26F3E" },
    { label: "Événements",     value: stats.events,         icon: CalendarDays, color: "#D4A574" },
    { label: "Suspendus",      value: stats.suspended,      icon: UserX,        color: "#B44F3F" },
  ];

  return (
    <div>
      <div className="mb-10">
        <p className="caption-label text-[#A26F3E]">Tableau de bord</p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1">Aperçu de la maison</h1>
        <p className="text-[#666] mt-2">Vue d'ensemble de l'activité Helencia</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="card-lux p-5" data-testid={`stat-${c.label.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="caption-label text-[#A26F3E]">{c.label}</span>
                <Icon className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <p className="font-serif text-4xl text-[#1A1A1A]">{c.value ?? 0}</p>
            </div>
          );
        })}
      </div>

      <div className="card-lux p-7">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="caption-label text-[#A26F3E]">Activité récente</p>
            <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">Dernières actions</h3>
          </div>
        </div>
        {activity.length === 0 ? (
          <p className="text-sm text-[#8C7D6B] py-6">Aucune activité pour le moment.</p>
        ) : (
          <ul className="divide-y divide-[#FAF3EA]">
            {activity.slice(0, 15).map((a, i) => (
              <li key={i} className="py-3.5 flex items-center gap-3 text-sm">
                <span className="w-2 h-2 rounded-full" style={{ background:
                  a.kind === "user_created" ? "#C68E59" :
                  a.kind === "collab_created" ? "#6E4B2A" : "#D4A574"
                }} />
                <span className="flex-1 text-[#1A1A1A]">{a.label}</span>
                <span className="text-[11px] text-[#8C7D6B]">
                  {new Date(a.at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ============================= USERS ============================= */
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [badgeDialog, setBadgeDialog] = useState(null);
  const [badgeInput, setBadgeInput] = useState("");

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (role !== "all" && u.role !== role) return false;
      if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [users, q, role]);

  const action = async (fn, label) => {
    try { await fn(); toast.success(label); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const grantPremium = (uid) => action(() => api.post(`/admin/premium/${uid}`, { days: 30 }), "Premium accordé (30j)");
  const revokePremium = (uid) => action(() => api.post(`/admin/revoke-premium/${uid}`), "Premium révoqué");
  const verifyPro = (uid, current) => action(() => current ? api.post(`/admin/unverify-pro/${uid}`) : api.post(`/admin/verify-pro/${uid}`), current ? "Vérification retirée" : "Créateur vérifié");
  const suspend = (uid) => {
    const reason = prompt("Raison de la suspension (optionnel) :") ?? "";
    action(() => api.post(`/admin/suspend/${uid}`, { reason }), "Compte suspendu");
  };
  const reactivate = (uid) => action(() => api.post(`/admin/reactivate/${uid}`), "Compte réactivé");
  const del = (uid) => {
    if (!window.confirm("Supprimer définitivement cet utilisateur ?")) return;
    action(() => api.delete(`/admin/users/${uid}`), "Utilisateur supprimé");
  };
  const addBadge = async () => {
    if (!badgeInput.trim()) return;
    try {
      await api.post(`/admin/badges/${badgeDialog.user_id}`, { badge: badgeInput.trim() });
      toast.success("Badge ajouté");
      setBadgeInput("");
      setBadgeDialog(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeBadge = async (uid, badge) => {
    await api.delete(`/admin/badges/${uid}/${encodeURIComponent(badge)}`);
    toast.success("Badge retiré");
    await load();
  };

  return (
    <div>
      <AdminAccountsCard />
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="caption-label text-[#A26F3E]">Utilisateurs</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Modération & gestion</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C7D6B]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher nom ou email…"
              className="pl-9 rounded-full w-64"
              data-testid="search-users-input"
            />
          </div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="rounded-full w-44" data-testid="role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les rôles</SelectItem>
              <SelectItem value="talent">Talent</SelectItem>
              <SelectItem value="professional">Créateur</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="card-lux overflow-hidden" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF3EA]">
            <tr className="text-left">
              <th className="p-4 caption-label">Nom</th>
              <th className="p-4 caption-label">Email</th>
              <th className="p-4 caption-label">Rôle</th>
              <th className="p-4 caption-label">Statut</th>
              <th className="p-4 caption-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.user_id} className="border-t border-[#EADDC9] hover:bg-[#FFFBF5]" data-testid={`user-row-${u.user_id}`}>
                <td className="p-4 font-semibold text-[#1A1A1A]">{u.name}</td>
                <td className="p-4 text-[#6E4B2A]">{u.email}</td>
                <td className="p-4 capitalize text-[#555]">{u.role || "—"}</td>
                <td className="p-4">
                  {u.suspended ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-[#F6E6E3] text-[#A3483F] border border-[#E6C0BA]">
                      Suspendu
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-[#E7F0E3] text-[#3F6A39] border border-[#BFD4B7]">
                      Actif
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex gap-1.5 justify-end flex-wrap">
                    <Link to={`/talents/${u.user_id}`} target="_blank">
                      <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#EADDC9]" title="Voir profil public">
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </Link>
                    {(u.role === "creator" || u.role === "professional") && (
                      <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#EADDC9]" onClick={() => verifyPro(u.user_id, false)} data-testid={`verify-${u.user_id}`}>
                        <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Vérifier
                      </Button>
                    )}
                    {u.role === "talent" && (
                      <>
                        <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#E6C29A] text-[#8B5A2B]" onClick={() => grantPremium(u.user_id)} data-testid={`premium-${u.user_id}`}>
                          <Crown className="w-3.5 h-3.5 mr-1" /> +30j
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#EADDC9]" onClick={() => revokePremium(u.user_id)}>
                          Révoquer
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#EADDC9]" onClick={() => setBadgeDialog(u)} data-testid={`badge-${u.user_id}`}>
                          <Award className="w-3.5 h-3.5 mr-1" /> Badge
                        </Button>
                      </>
                    )}
                    {u.suspended ? (
                      <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#BFD4B7] text-[#3F6A39]" onClick={() => reactivate(u.user_id)} data-testid={`reactivate-${u.user_id}`}>
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Réactiver
                      </Button>
                    ) : u.role !== "admin" && (
                      <Button size="sm" variant="outline" className="rounded-full h-8 px-3 border-[#E6C0BA] text-[#A3483F]" onClick={() => suspend(u.user_id)} data-testid={`suspend-${u.user_id}`}>
                        <UserX className="w-3.5 h-3.5 mr-1" /> Suspendre
                      </Button>
                    )}
                    {u.role !== "admin" && (
                      <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 border-[#E6C0BA] text-[#A3483F]" onClick={() => del(u.user_id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Badge dialog */}
      <Dialog open={!!badgeDialog} onOpenChange={(o) => !o && setBadgeDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Badges de {badgeDialog?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <Label className="caption-label">Badges actuels</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-5 min-h-[32px]">
              {(badgeDialog && users.find((u) => u.user_id === badgeDialog.user_id)?.badges || []).length === 0 && (
                <p className="text-xs text-[#8C7D6B]">Aucun badge pour le moment.</p>
              )}
              {badgeDialog && (
                <BadgeChips
                  uid={badgeDialog.user_id}
                  onRemove={(b) => removeBadge(badgeDialog.user_id, b)}
                />
              )}
            </div>
            <Label className="caption-label">Ajouter un badge personnalisé</Label>
            <div className="flex gap-2 mt-2">
              <Input value={badgeInput} onChange={(e) => setBadgeInput(e.target.value)} placeholder="VIP, Ambassadeur, Cover Star…" className="rounded-xl" data-testid="badge-input" />
              <Button onClick={addBadge} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="badge-add-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {["VIP", "Ambassadeur", "Cover Star", "Espoir", "Icône"].map((s) => (
                <button
                  key={s}
                  onClick={() => setBadgeInput(s)}
                  className="px-3 py-1 rounded-full text-[11px] border border-dashed border-[#D4A574] text-[#8B5A2B] hover:bg-[#FAF3EA]"
                >
                  + {s}
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BadgeChips({ uid, onRemove }) {
  const [badges, setBadges] = useState([]);
  useEffect(() => {
    api.get(`/talents/${uid}`).then(({ data }) => setBadges(data.profile?.badges || []));
  }, [uid]);
  if (badges.length === 0) return null;
  return (
    <>
      {badges.map((b) => (
        <span key={b} className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full text-xs bg-[#FAF3EA] border border-[#E6C29A] text-[#8B5A2B]">
          {b}
          <button onClick={() => onRemove(b)} className="hover:bg-[#E6C29A] rounded-full w-4 h-4 flex items-center justify-center">
            <X className="w-2.5 h-2.5" />
          </button>
        </span>
      ))}
    </>
  );
}

/* ============================= COLLABORATIONS ============================= */
function CollabsPanel() {
  const [collabs, setCollabs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");

  const load = () => {
    const params = statusFilter !== "all" ? { status: statusFilter } : {};
    api.get("/admin/collaborations", { params }).then(({ data }) => setCollabs(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const changeStatus = async (id, status) => {
    await api.put(`/admin/collaborations/${id}/status`, { status });
    toast.success("Statut mis à jour");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cette collaboration ? Toutes les candidatures seront perdues.")) return;
    await api.delete(`/admin/collaborations/${id}`);
    toast.success("Collaboration supprimée");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="caption-label text-[#A26F3E]">Collaborations</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Modération des projets</h1>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-full w-48" data-testid="collab-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="draft">Brouillon</SelectItem>
            <SelectItem value="active">Actif</SelectItem>
            <SelectItem value="closed">Clôturé</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {collabs.length === 0 && (
          <div className="card-lux p-10 text-center text-sm text-[#8C7D6B]">Aucune collaboration.</div>
        )}
        {collabs.map((c) => (
          <div key={c.id} className="card-lux p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`collab-row-${c.id}`}>
            <div className="md:col-span-5">
              <p className="caption-label text-[#A26F3E] capitalize">{c.project_type}</p>
              <h4 className="font-serif text-xl text-[#1A1A1A] mt-1 leading-tight">{c.title}</h4>
              <p className="text-xs text-[#8C7D6B] mt-1">
                par {c.pro?.name || "—"} {c.pro_profile?.verified && <VerifiedBadge className="ml-1" />}
              </p>
            </div>
            <div className="md:col-span-3 text-xs text-[#555]">
              <p>{c.location}</p>
              <p className="text-[#8C7D6B] mt-0.5">{c.application_count} candidature{c.application_count > 1 ? "s" : ""}</p>
            </div>
            <div className="md:col-span-2">
              <Select value={c.status} onValueChange={(v) => changeStatus(c.id, v)}>
                <SelectTrigger className="rounded-full h-9" data-testid={`collab-status-${c.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Brouillon</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="closed">Clôturé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex gap-2 justify-end">
              <Button size="sm" variant="outline" className="rounded-full h-9 px-3 border-[#E6C0BA] text-[#A3483F]" onClick={() => del(c.id)} data-testid={`collab-delete-${c.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================= EVENTS ============================= */
function EventsPanel() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState("form");
  const empty = { title: "", description: "", date: "", time: "", location: "", image_url: "", event_type: "casting", status: "upcoming", visibility: "public", capacity: "", requires_approval: false };
  const [form, setForm] = useState(empty);

  const load = () => api.get("/events").then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setStep("form"); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title, description: e.description, date: (e.date || "").slice(0, 10),
      time: e.time || "",
      location: e.location, image_url: e.image_url || "",
      event_type: e.event_type, status: e.status || "upcoming",
      visibility: e.visibility || "public",
      capacity: e.capacity || "",
      requires_approval: !!e.requires_approval,
    });
    setStep("form");
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        capacity: form.capacity === "" || form.capacity === null ? null : Number(form.capacity),
      };
      let savedId;
      if (editing) {
        await api.put(`/events/${editing.id}`, payload);
        savedId = editing.id;
      } else {
        const { data } = await api.post("/events", payload);
        savedId = data.id;
      }
      toast.success(editing ? "Événement mis à jour" : "Événement créé");
      await load();
      const fresh = (await api.get("/events")).data.find((x) => x.id === savedId);
      setEditing(fresh);
      setStep("images");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/events/${id}`);
    load();
  };
  const onImagesChange = async () => {
    await load();
    const fresh = (await api.get("/events")).data.find((x) => x.id === editing.id);
    setEditing(fresh);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="caption-label text-[#A26F3E]">Événements</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Agenda mode</h1>
        </div>
        <Button onClick={openNew} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="new-event-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvel événement
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {events.map((e) => (
          <div key={e.id} className="card-lux overflow-hidden" data-testid={`event-card-${e.id}`}>
            <div className="aspect-[16/10] overflow-hidden bg-[#FAF3EA] relative">
              {e.cover_image ? (
                <img src={`${API}/files/${e.cover_image}`} alt="" className="w-full h-full object-cover" />
              ) : e.image_url ? (
                <img src={e.image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#B3A78C] text-xs">Sans image</div>
              )}
              {e.gallery_images?.length > 0 && (
                <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px]">
                  +{e.gallery_images.length}
                </span>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between gap-2">
                <p className="caption-label text-[#A26F3E]">{labelEventType(e.event_type)}</p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide ${e.status === "upcoming" ? "bg-[#E7F0E3] text-[#3F6A39]" : "bg-[#FAF3EA] text-[#8C7D6B]"}`}>{e.status}</span>
              </div>
              <h4 className="font-serif text-xl text-[#1A1A1A] leading-tight mt-1">{e.title}</h4>
              <p className="text-xs text-[#6E4B2A] mt-2">{e.location} · {new Date(e.date).toLocaleDateString("fr-FR")}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-full border-[#EADDC9]" onClick={() => openEdit(e)} data-testid={`edit-event-${e.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                </Button>
                <Link to={`/admin/events/${e.id}/participants`}>
                  <Button size="sm" variant="outline" className="rounded-full border-[#EADDC9]" data-testid={`manage-participants-${e.id}`}>
                    <Users className="w-3.5 h-3.5 mr-1" /> Participants
                  </Button>
                </Link>
                <Button size="sm" variant="outline" className="rounded-full border-[#E6C0BA] text-[#A3483F]" onClick={() => del(e.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="mt-3 pt-3 border-t border-[#FAF3EA]">
                <DashboardHeroToggle kind="event" targetId={e.id} compact />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {step === "form" ? (editing ? "Modifier l'événement" : "Nouvel événement") : "Images de l'événement"}
            </DialogTitle>
          </DialogHeader>
          {step === "form" ? (
            <div className="space-y-4 py-2">
              <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid="event-title-input" />
              <Textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" />
              <div className="grid grid-cols-3 gap-3">
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl" data-testid="event-date-input" />
                <Input type="time" value={form.time || ""} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-xl" data-testid="event-time-input" placeholder="Heure" />
                <Input placeholder="Lieu" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upcoming">À venir</SelectItem>
                    <SelectItem value="past">Passé</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#FAF3EA]">
                <div>
                  <p className="caption-label mb-2">Visibilité</p>
                  <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="event-visibility-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public — ouvert à tous</SelectItem>
                      <SelectItem value="premium">Premium uniquement</SelectItem>
                      <SelectItem value="private">Privé — sur invitation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="caption-label mb-2">Capacité (laisser vide = illimitée)</p>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Ex. 50"
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    className="rounded-xl"
                    data-testid="event-capacity-input"
                  />
                </div>
              </div>
              <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#FAF3EA] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.requires_approval}
                  onChange={(e) => setForm({ ...form, requires_approval: e.target.checked })}
                  className="w-4 h-4 accent-[#C68E59]"
                  data-testid="event-requires-approval"
                />
                <div>
                  <p className="text-sm text-[#1A1A1A]">Approbation manuelle des participants</p>
                  <p className="text-xs text-[#666]">Chaque inscription passera en "En attente" avant validation admin.</p>
                </div>
              </label>
            </div>
          ) : (
            <div className="py-2">
              <p className="text-sm text-[#6E4B2A] mb-5">
                Ajoutez une image principale, 3 à 6 photos galerie, une affiche, et des photos d'éditions précédentes.
              </p>
              <ResourceImageUploader
                resourceType="events"
                resourceId={editing.id}
                coverImage={editing.cover_image}
                galleryImages={editing.gallery_images || []}
                posterImage={editing.poster_image}
                pastGallery={editing.event_gallery || []}
                allowPoster
                allowPast
                max={{ gallery: 6, past: 8 }}
                onChange={onImagesChange}
              />
            </div>
          )}
          <DialogFooter>
            {step === "form" ? (
              <>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annuler</Button>
                <Button onClick={save} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="save-event-btn">
                  {editing ? "Enregistrer" : "Créer & ajouter images"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setStep("form")} className="rounded-full">Retour</Button>
                <Button onClick={() => setOpen(false)} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="event-images-done-btn">
                  Terminer
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================= CONCOURS ============================= */
function ConcoursPanel() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { title: "", theme: "", description: "", prize: "", image_url: "", rules: "", start_date: "", end_date: "", status: "active" };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  // Pending file when creating (concours not yet saved) -> uploaded right after creation
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);

  const load = () => api.get("/concours").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const resetPending = () => {
    setPendingFile(null);
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingPreview(null);
  };

  const openNew = () => { setEditing(null); setForm(empty); resetPending(); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...empty, ...c, start_date: (c.start_date || "").slice(0, 10), end_date: (c.end_date || "").slice(0, 10) });
    resetPending();
    setOpen(true);
  };

  const uploadFileToConcours = async (cid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(`/concours/${cid}/image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const save = async () => {
    try {
      setUploading(true);
      if (editing) {
        await api.put(`/admin/concours/${editing.id}`, form);
        if (pendingFile) {
          await uploadFileToConcours(editing.id, pendingFile);
        }
        toast.success("Concours mis à jour");
        setOpen(false); resetPending(); load();
      } else {
        const { data } = await api.post("/concours", form);
        if (pendingFile) {
          await uploadFileToConcours(data.id, pendingFile);
        }
        toast.success("Concours créé");
        setOpen(false); resetPending(); load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setUploading(false);
    }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce concours ?")) return;
    await api.delete(`/concours/${id}`);
    load();
  };

  const handleSelectFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Fichier image requis"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("Image trop volumineuse (max 15 Mo)"); return; }

    // If editing an existing concours -> upload immediately
    if (editing) {
      setUploading(true);
      try {
        const data = await uploadFileToConcours(editing.id, file);
        setForm((f) => ({ ...f, image_url: data.image_url }));
        setEditing((prev) => ({ ...prev, image_url: data.image_url }));
        toast.success("Image ajoutée");
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Erreur d'upload");
      } finally {
        setUploading(false);
      }
      return;
    }

    // New concours -> keep file pending, show local preview
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
  };

  const removeImage = async () => {
    // New concours with pending file
    if (!editing && pendingFile) {
      resetPending();
      return;
    }
    if (!editing) return;
    if (!window.confirm("Retirer l'image de ce concours ?")) return;
    try {
      await api.delete(`/concours/${editing.id}/image`);
      setForm((f) => ({ ...f, image_url: "" }));
      setEditing((prev) => ({ ...prev, image_url: null }));
      toast.success("Image retirée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const previewUrl = pendingPreview || form.image_url;
  const hasImage = Boolean(previewUrl);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="caption-label text-[#A26F3E]">Concours</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Compétitions du mois</h1>
        </div>
        <Button onClick={openNew} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="new-concours-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau concours
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {items.map((c) => (
          <div key={c.id} className="card-lux overflow-hidden" data-testid={`concours-card-${c.id}`}>
            {c.image_url && <div className="aspect-[16/9] overflow-hidden bg-[#FAF3EA]"><img src={c.image_url} alt="" className="w-full h-full object-cover" /></div>}
            <div className="p-5">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-[#C68E59]" />
                <p className="caption-label text-[#A26F3E]">{c.theme}</p>
              </div>
              <h4 className="font-serif text-xl text-[#1A1A1A] leading-tight mt-1">{c.title}</h4>
              {c.prize && <p className="text-xs text-[#6E4B2A] mt-2">🏆 {c.prize}</p>}
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="rounded-full border-[#EADDC9]" onClick={() => openEdit(c)} data-testid={`edit-concours-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                </Button>
                <Button size="sm" variant="outline" className="rounded-full border-[#E6C0BA] text-[#A3483F]" onClick={() => del(c.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="mt-3 pt-3 border-t border-[#FAF3EA]">
                <DashboardHeroToggle kind="contest" targetId={c.id} compact />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Modifier concours" : "Nouveau concours"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" data-testid="concours-title-input" />
            <Input placeholder="Thème" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="rounded-xl" />
            <Textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" />
            <Textarea placeholder="Règles de participation (optionnel)" rows={3} value={form.rules || ""} onChange={(e) => setForm({ ...form, rules: e.target.value })} className="rounded-xl" data-testid="concours-rules-input" />
            <Input placeholder="Récompense" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} className="rounded-xl" />

            {/* Image block */}
            <div className="rounded-2xl border border-[#EADDC9] bg-[#FFFBF5] p-4">
              <p className="caption-label text-[#A26F3E] mb-3">Image du concours</p>
              {hasImage ? (
                <div className="relative">
                  <div className="aspect-[16/9] overflow-hidden rounded-xl bg-[#FAF3EA]">
                    <img src={previewUrl} alt="Aperçu" className="w-full h-full object-cover" data-testid="concours-image-preview" />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <label
                      htmlFor="concours-image-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-sm cursor-pointer transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                      data-testid="concours-upload-image-label"
                    >
                      <Pencil className="w-4 h-4" />
                      {uploading ? "Téléversement…" : "Remplacer l'image"}
                    </label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={removeImage}
                      className="rounded-full border-[#E6C0BA] text-[#A3483F]"
                      data-testid="concours-remove-image-btn"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Retirer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-start gap-3">
                  <label
                    htmlFor="concours-image-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-sm cursor-pointer transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                    data-testid="concours-upload-image-label"
                  >
                    <Plus className="w-4 h-4" />
                    {uploading ? "Téléversement…" : "Choisir une image depuis mes fichiers"}
                  </label>
                  <p className="text-[11px] text-[#6E4B2A]">JPG / PNG — max 15 Mo. Compressée automatiquement.</p>
                </div>
              )}
              <input
                id="concours-image-upload"
                type="file"
                accept="image/*"
                onChange={handleSelectFile}
                className="hidden"
                data-testid="concours-image-file-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl" />
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" disabled={uploading}>Fermer</Button>
            <Button onClick={save} disabled={uploading} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="save-concours-btn">
              {uploading ? "Enregistrement…" : (editing ? "Enregistrer" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================= CONTEST ENTRIES (MODÉRATION) ============================= */
function ContestEntriesPanel() {
  const [items, setItems] = useState([]);
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterContest, setFilterContest] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [reasonOpen, setReasonOpen] = useState(null); // entry being rejected
  const [reason, setReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterContest !== "all") params.contest_id = filterContest;
      if (filterStatus !== "all") params.status = filterStatus;
      const { data } = await api.get("/admin/contest-entries", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/concours").then(({ data }) => setContests(Array.isArray(data) ? data : []));
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterContest, filterStatus]);

  const moderate = async (eid, status, reasonText = null) => {
    try {
      await api.put(`/admin/contest-entries/${eid}`, {
        status,
        ...(reasonText ? { reason: reasonText } : {}),
      });
      toast.success(status === "rejected" ? "Participation masquée" : "Participation restaurée");
      setReasonOpen(null);
      setReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const counts = useMemo(() => ({
    all: items.length,
    approved: items.filter((e) => e.status === "approved").length,
    rejected: items.filter((e) => e.status === "rejected").length,
  }), [items]);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="caption-label text-[#A26F3E]">Modération</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-1">Participations aux concours</h1>
          <p className="text-sm text-[#6E4B2A] mt-2">
            Approuver ou masquer les participations soumises par les talents.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select value={filterContest} onValueChange={setFilterContest}>
          <SelectTrigger className="w-full sm:w-72 rounded-full bg-white border-[#EADDC9]" data-testid="filter-contest">
            <SelectValue placeholder="Tous les concours" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les concours</SelectItem>
            {contests.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-56 rounded-full bg-white border-[#EADDC9]" data-testid="filter-status">
            <SelectValue placeholder="Tous statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="approved">Approuvées</SelectItem>
            <SelectItem value="rejected">Masquées</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2 text-xs text-[#6E4B2A]">
          <span className="px-3 py-1.5 rounded-full bg-white border border-[#EADDC9]">
            Total : <span className="font-semibold text-[#1A1A1A]">{counts.all}</span>
          </span>
          <span className="px-3 py-1.5 rounded-full bg-[#E7F5EC] text-[#2E7D32] border border-[#BFE4C5]">
            Approuvées : {counts.approved}
          </span>
          <span className="px-3 py-1.5 rounded-full bg-[#FCE9E7] text-[#A3483F] border border-[#E6C0BA]">
            Masquées : {counts.rejected}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl bg-white border border-dashed border-[#EADDC9] p-12 text-center">
          <ImageIcon className="w-10 h-10 text-[#C68E59] mx-auto opacity-60" />
          <p className="text-sm text-[#8B8680] mt-4">Aucune participation à modérer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="entries-grid">
          {items.map((e) => (
            <ModEntryCard
              key={e.id}
              entry={e}
              onReject={() => { setReasonOpen(e); setReason(""); }}
              onApprove={() => moderate(e.id, "approved")}
            />
          ))}
        </div>
      )}

      {/* Reject with reason dialog */}
      <Dialog open={!!reasonOpen} onOpenChange={(v) => { if (!v) setReasonOpen(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Masquer cette participation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#4A4A4A]">
              Le talent recevra une notification expliquant la raison du retrait.
            </p>
            <Label className="caption-label">Raison (optionnelle)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(ev) => setReason(ev.target.value)}
              placeholder="Ex. Image hors thème, droits non respectés…"
              className="rounded-xl"
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonOpen(null)} className="rounded-full">Annuler</Button>
            <Button
              onClick={() => moderate(reasonOpen.id, "rejected", reason.trim() || null)}
              className="rounded-full bg-[#A3483F] hover:bg-[#7E342E] text-white"
              data-testid="reject-confirm-btn"
            >
              <EyeOff className="w-4 h-4 mr-2" /> Masquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModEntryCard({ entry, onReject, onApprove }) {
  const u = entry.user || {};
  const c = entry.contest || {};
  const isRejected = entry.status === "rejected";
  return (
    <div className={`bg-white rounded-2xl border overflow-hidden flex flex-col ${isRejected ? "border-[#E6C0BA] opacity-90" : "border-[#EADDC9]"}`} data-testid={`mod-entry-${entry.id}`}>
      <div className="aspect-square overflow-hidden bg-[#FAF3EA] relative">
        <img src={entry.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
        {isRejected && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-[#A3483F]">
              Masquée
            </span>
          </div>
        )}
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/95 px-2 py-1 rounded-full text-xs font-semibold text-[#1A1A1A]">
          <Heart className="w-3 h-3 text-[#C68E59]" /> {entry.votes_count ?? 0}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-wider text-[#A26F3E] line-clamp-1">
          {c.title || "Concours"}
        </div>
        <div className="flex items-center gap-2 mt-2">
          {u.picture ? (
            <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-[#FAF3EA] flex items-center justify-center text-[10px] font-semibold text-[#A26F3E]">
              {(u.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#1A1A1A] truncate">{u.name}</div>
            {u.email && <div className="text-[10px] text-[#8B8680] truncate">{u.email}</div>}
          </div>
        </div>
        {entry.description && (
          <p className="text-[11px] text-[#6E4B2A] mt-2 line-clamp-2 leading-snug">{entry.description}</p>
        )}
        {entry.moderation_reason && isRejected && (
          <div className="mt-2 text-[11px] text-[#A3483F] bg-[#FCE9E7] border border-[#E6C0BA] rounded-lg px-2 py-1.5 line-clamp-2">
            « {entry.moderation_reason} »
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Link to={`/contest/${entry.contest_id}/entry/${entry.id}`} target="_blank" className="flex-1">
            <Button variant="outline" size="sm" className="w-full rounded-full border-[#EADDC9] h-9 text-xs">
              <ExternalLink className="w-3 h-3 mr-1" /> Voir
            </Button>
          </Link>
          {isRejected ? (
            <Button
              size="sm"
              onClick={onApprove}
              className="flex-1 rounded-full bg-[#2E7D32] hover:bg-[#1F5723] text-white h-9 text-xs"
              data-testid={`mod-approve-${entry.id}`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" /> Restaurer
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onReject}
              className="flex-1 rounded-full bg-[#A3483F] hover:bg-[#7E342E] text-white h-9 text-xs"
              data-testid={`mod-reject-${entry.id}`}
            >
              <EyeOff className="w-3 h-3 mr-1" /> Masquer
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================= BROADCAST ============================= */
function BroadcastPanel() {
  const [form, setForm] = useState({
    target: "all", title: "", body: "", link: "",
    channels: ["in_app"],
  });
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [audience, setAudience] = useState(null);

  const refresh = async () => {
    try {
      const [{ data: list }, { data: aud }] = await Promise.all([
        api.get("/admin/broadcasts"),
        api.get("/admin/broadcasts/audience-counts"),
      ]);
      setHistory(Array.isArray(list) ? list : []);
      setAudience(aud);
    } catch { /* no-op */ }
  };
  useEffect(() => { refresh(); }, []);

  const toggleChannel = (c) => {
    setForm((f) => {
      const has = f.channels.includes(c);
      const next = has ? f.channels.filter((x) => x !== c) : [...f.channels, c];
      return { ...f, channels: next.length ? next : ["in_app"] };
    });
  };

  const applyTemplate = (tpl) => setForm((f) => ({ ...f, ...tpl }));

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Titre et message requis");
      return;
    }
    const audienceLabel = {
      all: "tous les utilisateurs",
      talent: "tous les talents",
      professional: "tous les créateurs",
      premium: "tous les Premium",
      verified_pro: "tous les créateurs vérifiés",
    }[form.target] || form.target;
    const channelLabel = form.channels.length === 2 ? "in-app + email" : form.channels[0] === "email" ? "email" : "in-app";
    if (!window.confirm(`Envoyer cette notification à ${audienceLabel} (${channelLabel}) ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", form);
      const parts = [];
      if (data.in_app_sent) parts.push(`${data.in_app_sent} notif in-app`);
      if (data.email_sent) parts.push(`${data.email_sent} email${data.email_sent > 1 ? "s" : ""}`);
      if (data.email_failed) parts.push(`${data.email_failed} échec(s) email`);
      toast.success(parts.length ? parts.join(" · ") : "Notification envoyée");
      setForm({ target: "all", title: "", body: "", link: "", channels: ["in_app"] });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSending(false); }
  };

  const audienceCount = audience?.[form.target] ?? 0;

  return (
    <div data-testid="admin-broadcast-page">
      <div className="mb-8">
        <p className="caption-label text-[#A26F3E]">Communication</p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-1 leading-[1.05]">Notifications globales</h1>
        <p className="text-[#666] mt-2 text-sm">Envoyez une annonce à tout ou partie des utilisateurs — in-app et/ou email.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="card-lux p-7 lg:col-span-2 space-y-5">
          {/* Quick templates */}
          <div>
            <p className="caption-label">Modèles rapides</p>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="broadcast-templates">
              <TemplateChip
                label="Concours en cours"
                onClick={() => applyTemplate({ target: "talent", title: "Un concours vous attend", body: "Un nouveau concours Helencia vient de s'ouvrir. Préparez votre meilleure série et candidatez avant la deadline.", link: "/concours" })}
              />
              <TemplateChip
                label="Événement à venir"
                onClick={() => applyTemplate({ target: "all", title: "Événement Helencia", body: "Un nouvel événement vient d'être publié. Réservez votre place dès maintenant.", link: "/events" })}
              />
              <TemplateChip
                label="Nouvelle collaboration"
                onClick={() => applyTemplate({ target: "talent", title: "Nouvelle collaboration ouverte", body: "Une marque vient de publier un projet. Découvrez-le et candidatez.", link: "/collaborations" })}
              />
              <TemplateChip
                label="Maintenance"
                onClick={() => applyTemplate({ target: "all", title: "Maintenance prévue", body: "Une courte maintenance technique est planifiée. Merci de votre patience.", link: "" })}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="caption-label">Cible</Label>
              <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                <SelectTrigger className="rounded-xl mt-2" data-testid="broadcast-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les utilisateurs{audience ? ` (${audience.all})` : ""}</SelectItem>
                  <SelectItem value="talent">Talents{audience ? ` (${audience.talent})` : ""}</SelectItem>
                  <SelectItem value="professional">Créateurs{audience ? ` (${audience.professional})` : ""}</SelectItem>
                  <SelectItem value="premium">Premium uniquement{audience ? ` (${audience.premium})` : ""}</SelectItem>
                  <SelectItem value="verified_pro">Créateurs vérifiés{audience ? ` (${audience.verified_pro})` : ""}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption-label">Canaux</Label>
              <div className="flex gap-2 mt-2">
                <ChannelChip
                  label="In-app"
                  active={form.channels.includes("in_app")}
                  onClick={() => toggleChannel("in_app")}
                  testid="channel-in-app"
                />
                <ChannelChip
                  label="Email"
                  active={form.channels.includes("email")}
                  onClick={() => toggleChannel("email")}
                  testid="channel-email"
                />
              </div>
            </div>
          </div>

          <div>
            <Label className="caption-label">Titre</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ex. Nouveau concours lancé !"
              className="rounded-xl mt-2"
              maxLength={120}
              data-testid="broadcast-title"
            />
            <p className="text-xs text-[#8B8680] mt-1">{form.title.length}/120</p>
          </div>
          <div>
            <Label className="caption-label">Message</Label>
            <Textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              rows={5}
              placeholder="Le texte du message…"
              className="rounded-xl mt-2"
              maxLength={1200}
              data-testid="broadcast-body"
            />
            <p className="text-xs text-[#8B8680] mt-1">{form.body.length}/1200</p>
          </div>
          <div>
            <Label className="caption-label">Lien (optionnel)</Label>
            <Input
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
              placeholder="/concours ou https://..."
              className="rounded-xl mt-2"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#FAF3EA]">
            <p className="text-xs text-[#666]">
              Audience estimée : <span className="font-semibold text-[#1A1A1A]">{audienceCount}</span> destinataire(s)
            </p>
            <Button
              onClick={send}
              disabled={sending}
              className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
              data-testid="broadcast-send-btn"
            >
              {sending ? "Envoi..." : (<><BellRing className="w-4 h-4 mr-2" /> Envoyer</>)}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="card-lux p-7 !bg-[#1A1A1A] text-white border-[#1A1A1A]">
          <p className="caption-label !text-[#E6C29A]">Aperçu</p>
          <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-5">
            <p className="text-xs text-white/60">Notification</p>
            <p className="font-serif text-xl mt-1 leading-tight">{form.title || "Titre de la notification"}</p>
            <p className="text-sm text-white/80 mt-2">{form.body || "Votre message apparaîtra ici…"}</p>
            {form.link && <p className="text-xs text-[#E6C29A] mt-3">→ {form.link}</p>}
          </div>
          <p className="text-[10px] uppercase tracking-wider text-white/40 mt-4">
            Canaux : {form.channels.join(" + ")}
          </p>
        </div>
      </div>

      {/* History */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-2xl text-[#1A1A1A]">Historique des envois</h2>
          <p className="text-xs text-[#8B8680]">{history.length} envoi{history.length > 1 ? "s" : ""}</p>
        </div>
        {history.length === 0 ? (
          <div className="card-lux p-10 text-center">
            <p className="text-sm text-[#666]">Aucun envoi pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="broadcast-history">
            {history.map((h) => <BroadcastHistoryRow key={h.id} h={h} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateChip({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs px-3 py-1.5 rounded-full bg-[#FAF3EA] hover:bg-[#F4E4C9] border border-[#EADDC9] text-[#6E4B2A] transition-colors"
      data-testid={`tpl-${label.replace(/\s/g, "-").toLowerCase()}`}
    >
      {label}
    </button>
  );
}

function ChannelChip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-sm px-4 py-2 rounded-full border transition-all ${
        active ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#1A1A1A] border-[#EADDC9] hover:border-[#1A1A1A]"
      }`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function BroadcastHistoryRow({ h }) {
  const dt = new Date(h.sent_at);
  const audienceLabel = {
    all: "Tous", talent: "Talents", professional: "Créateurs",
    premium: "Premium", verified_pro: "Créateurs vérifiés",
  }[h.target] || h.target;
  return (
    <div className="card-lux p-4 flex items-start gap-4" data-testid={`broadcast-row-${h.id}`}>
      <div className="w-10 h-10 rounded-xl bg-[#FAF3EA] flex items-center justify-center text-[#C68E59] flex-shrink-0">
        <BellRing className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-serif text-base text-[#1A1A1A] truncate">{h.title}</p>
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1A1A1A] text-white">
            {audienceLabel}
          </span>
          {(h.channels || []).map((c) => (
            <span key={c} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FAF3EA] text-[#A26F3E]">
              {c.replace("_", "-")}
            </span>
          ))}
        </div>
        <p className="text-xs text-[#666] mt-1 line-clamp-2">{h.body}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#8B8680] mt-2">
          <span>{h.recipients_count} destinataire(s)</span>
          {h.in_app_sent ? <span>· {h.in_app_sent} in-app</span> : null}
          {h.email_sent ? <span>· {h.email_sent} email</span> : null}
          {h.email_failed ? <span className="text-[#B44F3F]">· {h.email_failed} échec(s)</span> : null}
          <span>· {dt.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</span>
          {h.sent_by_name && <span>· par {h.sent_by_name}</span>}
        </div>
      </div>
    </div>
  );
}

/* ============================= ROOT ============================= */
export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && user.role !== "admin") navigate("/");
  }, [user, loading, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <AdminLayout>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="featured" element={<AdminFeatured />} />
        <Route path="dashboard-control" element={<AdminDashboardControl />} />
        <Route path="concours-control" element={<AdminConcoursV2 />} />
        <Route path="moderation" element={<AdminModeration />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="cms" element={<AdminCMS />} />
        <Route path="events/:id/participants" element={<AdminEventParticipants />} />
        <Route path="users" element={<UsersPanel />} />
        <Route path="collaborations" element={<CollabsPanel />} />
        <Route path="events" element={<EventsPanel />} />
        <Route path="concours" element={<ConcoursPanel />} />
        <Route path="contest-entries" element={<ContestEntriesPanel />} />
        <Route path="broadcast" element={<BroadcastPanel />} />
        <Route path="purge" element={<AdminPurge />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminLayout>
  );
}
